"use strict";
/**
 * `serviceToContent` — the single style-agnostic, provider-agnostic mapping from
 * a `ServicePayload` (+ flight context) to a presentation model (spec §3, §5).
 *
 * This is the layer that REPLACES the per-kind `throw`s in the Apple adapter and
 * is the reference both the Apple per-style adapter and the Google per-style
 * adapter arrange into a concrete layout. It knows every kind's canonical fields;
 * adding a Wallet style does NOT touch this file (spec §3).
 *
 * Lives in wallet-core because `@insurwiz/wallet-core` is the only package both
 * `wallet-apple` and `wallet-google` already depend on, and the model is
 * provider-agnostic by design.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.serviceToContent = serviceToContent;
const formatLocalDateTime_js_1 = require("./formatLocalDateTime.js");
function formatDateTime(iso) {
    return (0, formatLocalDateTime_js_1.formatLocalDateTime)(iso);
}
function maskCardNumber(card) {
    if (card.length <= 4)
        return card;
    return `•••• ${card.slice(-4)}`;
}
function formatEur(minor) {
    const major = minor / 100;
    return `€${major.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}
function serviceToContent(service, flight) {
    switch (service.kind) {
        case 'lounge':
            return {
                title: 'Lounge Access',
                fields: [
                    { key: 'lounge', label: 'Lounge', value: service.loungeName },
                    { key: 'lounge_code', label: 'Lounge Code', value: service.loungeCode },
                    {
                        key: 'access_window',
                        label: 'Access Window',
                        value: `${formatDateTime(service.accessWindow.from)} – ${formatDateTime(service.accessWindow.to)}`,
                    },
                ],
                deliverable: { kind: 'qr' },
                venue: flight.departureAirport,
                flightSemantics: {
                    carrier: flight.carrier,
                    number: flight.number,
                    departureDate: flight.scheduledDeparture,
                },
            };
        case 'fast_track':
            return {
                title: 'Fast Track',
                fields: [
                    { key: 'lane', label: 'Lane', value: service.laneName },
                    { key: 'terminal', label: 'Terminal', value: service.terminal },
                ],
                deliverable: { kind: 'qr' },
                venue: service.terminal,
                flightSemantics: {
                    carrier: flight.carrier,
                    number: flight.number,
                    departureDate: flight.scheduledDeparture,
                },
            };
        case 'esim':
            return {
                title: 'Travel eSIM',
                fields: [
                    { key: 'plan', label: 'Data Plan', value: service.dataPlanName },
                    { key: 'iccid', label: 'ICCID', value: service.iccid },
                ],
                deliverable: { kind: 'url', url: service.activationUrl },
            };
        case 'edenred':
            return {
                title: 'Travel Card',
                fields: [
                    { key: 'card', label: 'Card', value: maskCardNumber(service.cardNumber) },
                    { key: 'category', label: 'Category', value: service.merchantCategory },
                    {
                        key: 'balance',
                        label: 'Balance',
                        value: `${String(service.balance.amount)} ${service.balance.currency}`,
                    },
                ],
                deliverable: { kind: 'none' },
                balance: { amount: service.balance.amount, currency: service.balance.currency },
            };
        case 'insurance': {
            const fields = [
                { key: 'policy', label: 'Policy', value: service.policyNumber },
                { key: 'insurer', label: 'Insurer', value: service.insurerName },
                { key: 'product', label: 'Product', value: service.productName },
                {
                    key: 'coverage',
                    label: 'Coverage',
                    value: `${formatDateTime(service.coverageStart)} – ${formatDateTime(service.coverageEnd)} · ${service.territory}`,
                },
                { key: 'assistance', label: '24/7 Assistance', value: service.assistancePhone },
            ];
            if (service.coverages.length > 0) {
                fields.push({
                    key: 'limits',
                    label: 'Key Coverages',
                    value: service.coverages.map((co) => `${co.label}: ${co.limit}`).join(' · '),
                });
            }
            return {
                title: 'Travel Insurance',
                fields,
                ...(service.claimUrl !== undefined
                    ? { deliverable: { kind: 'url', url: service.claimUrl } }
                    : { deliverable: { kind: 'qr' } }),
            };
        }
        case 'motor_kotelezo': {
            const fields = [
                { key: 'insurer', label: 'Insurer', value: service.insurerName },
                { key: 'plate', label: 'Plate', value: service.licensePlate },
                { key: 'vehicle', label: 'Vehicle', value: service.vehicleMakeModel },
                { key: 'vin', label: 'VIN', value: service.vin },
                { key: 'policy', label: 'Policy', value: service.policyNumber },
                { key: 'claim', label: '24/7 Claim Hotline', value: service.claimHotline },
            ];
            if (service.greenCardNumber !== undefined) {
                fields.push({ key: 'greencard', label: 'Green Card', value: service.greenCardNumber });
            }
            return {
                title: 'Motor — KGFB',
                fields,
                ...(service.claimUrl !== undefined
                    ? { deliverable: { kind: 'url', url: service.claimUrl } }
                    : { deliverable: { kind: 'qr' } }),
            };
        }
        case 'motor_casco': {
            const fields = [
                { key: 'insurer', label: 'Insurer', value: service.insurerName },
                { key: 'plate', label: 'Plate', value: service.licensePlate },
                { key: 'vehicle', label: 'Vehicle', value: service.vehicleMakeModel },
                { key: 'vin', label: 'VIN', value: service.vin },
                { key: 'policy', label: 'Policy', value: service.policyNumber },
                {
                    key: 'limits',
                    label: 'Deductible / Total Limit',
                    value: `${formatEur(service.deductibleMinor)} / ${formatEur(service.totalLimitMinor)}`,
                },
                { key: 'claim', label: '24/7 Claim Hotline', value: service.claimHotline },
            ];
            return {
                title: 'Motor — Casco',
                fields,
                ...(service.claimUrl !== undefined
                    ? { deliverable: { kind: 'url', url: service.claimUrl } }
                    : { deliverable: { kind: 'qr' } }),
            };
        }
        case 'life_accident':
            return {
                title: 'Life / Accident',
                fields: [
                    { key: 'insurer', label: 'Insurer', value: service.insurerName },
                    { key: 'product', label: 'Product', value: service.productName },
                    {
                        key: 'sum_insured',
                        label: 'Sum Insured',
                        value: formatEur(service.coverageAmountMinor),
                    },
                    { key: 'beneficiary', label: 'Beneficiary', value: service.beneficiaryName },
                    { key: 'policy', label: 'Policy', value: service.policyNumber },
                    { key: 'claim', label: '24/7 Claim Hotline', value: service.claimHotline },
                ],
                ...(service.claimUrl !== undefined
                    ? { deliverable: { kind: 'url', url: service.claimUrl } }
                    : { deliverable: { kind: 'qr' } }),
            };
    }
}
//# sourceMappingURL=serviceToContent.js.map